import { useMemo } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { type OrderWithContact } from '@/data/mockOrdersData'
import { OrderAmountAndTags } from './OrderAmountAndTags'
import { formatActivityString } from '@/utils/formatActivity'
import { formatSmartCurrency } from '@/utils/formatCurrency'
import { toast } from 'sonner'
import {
  KanbanProvider,
  KanbanBoard,
  KanbanHeader,
  KanbanCards,
  KanbanCard,
} from '@/components/ui/shadcn-io/kanban'

interface OrdersKanbanShadcnProps {
  orders: OrderWithContact[]
  onSelectOrder: (order: OrderWithContact) => void
  selectedOrderId?: string
  isLoading?: boolean
  onOrderStatusChange: (orderId: string, newStatus: string) => Promise<void>
  visibleStages?: string[]
  sortMode?: string
}

const ORDER_STAGES = [
  { id: 'new_lead', name: 'New Lead', label: 'New Lead', color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-200' },
  { id: 'qualified', name: 'Qualified', label: 'Qualified', color: 'bg-emerald-200 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-200' },
  { id: 'scheduled', name: 'Scheduled', label: 'Scheduled', color: 'bg-emerald-300 text-emerald-800 dark:bg-emerald-700 dark:text-emerald-200' },
  { id: 'completed', name: 'Completed', label: 'Completed', color: 'bg-emerald-400 text-emerald-900 dark:bg-emerald-600 dark:text-emerald-200' },
  { id: 'lost', name: 'Lost', label: 'Lost', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
] as const

// Transform orders to kanban format - extends Record<string, unknown> for Kanban compatibility
interface KanbanOrder extends OrderWithContact, Record<string, unknown> {
  column: string
  name: string
}

function OrderCard({ order, onSelectOrder, selectedOrderId }: { order: KanbanOrder; onSelectOrder: (order: OrderWithContact) => void; selectedOrderId?: string }) {
  const handleClick = (e: React.MouseEvent) => {
    // Prevent click during drag operations
    if (e.defaultPrevented) {
      return
    }
    
    onSelectOrder(order)
  }

  return (
    <KanbanCard
      id={order.id}
      name={order.contact.display_name}
      column={order.column}
      className={`transition-all hover:shadow-md ${
        selectedOrderId === order.id ? 'ring-2 ring-ring/50' : ''
      }`}
      onClick={handleClick}
    >
      {/* Row 1: Contact profile avatar, name, and preferred contact */}
      <div className="flex items-center gap-3 mb-3">
        <Avatar className="h-9 w-9 shrink-0">
          <AvatarImage src="" alt={order.contact.display_name} />
          <AvatarFallback className="text-sm">
            {order.contact.display_name.split(' ').map(n => n[0]).join('').toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-base truncate">{order.contact.display_name}</h4>
            <p className="text-sm text-muted-foreground truncate">
              {order.contact.preferred_contact_method === 'email' 
                ? order.contact.email 
                : order.contact.phone_raw}
            </p>
        </div>
      </div>

      {/* Row 2: Order amount and interest tags */}
      <div className="mb-3">
        <OrderAmountAndTags 
          valueCents={order.value_cents || 0} 
          tags={order.contact.tags || []} 
        />
      </div>

      {/* Row 3: Last activity type, time, and description */}
      {order.lastActivity && (() => {
        const activityData = formatActivityString(order.lastActivity)
        const IconComponent = activityData.icon
        return (
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            {/* Icon column - matches avatar width (36px) and centered */}
            <div className="w-9 flex justify-center">
              <IconComponent className="h-4.5 w-4.5" />
            </div>
            {/* Text column - left-aligned with name/contact info */}
            <div className="flex-1">
              <span className="truncate">
                {activityData.text}
              </span>
            </div>
          </div>
        )
      })()}
    </KanbanCard>
  )
}

export function OrdersKanbanShadcn({
  orders,
  onSelectOrder,
  selectedOrderId,
  isLoading,
  onOrderStatusChange,
  visibleStages = ['new_lead', 'qualified', 'scheduled', 'completed', 'lost'],
  sortMode: _sortMode = 'manual'
}: OrdersKanbanShadcnProps) {
  
  // Filter stages to only show visible ones
  const visibleOrderStages = ORDER_STAGES.filter(stage => visibleStages.includes(stage.id))

  // Transform orders to kanban format
  const kanbanOrders: KanbanOrder[] = useMemo(() => {
    return orders.map(order => ({
      ...order,
      column: order.stage,
      name: order.contact.display_name,
    }))
  }, [orders])

  // Calculate total values for each stage
  const stageTotals = useMemo(() => {
    return visibleOrderStages.reduce((acc, stage) => {
      const stageOrders = orders.filter(order => order.stage === stage.id)
      const total = stageOrders.reduce((sum, order) => sum + (order.value_cents || 0), 0)
      acc[stage.id] = total
      return acc
    }, {} as Record<string, number>)
  }, [orders, visibleOrderStages])

  const handleDataChange = async (newData: KanbanOrder[]) => {
    // Find orders that changed columns
    const changedOrders = newData.filter(newOrder => {
      const originalOrder = orders.find(o => o.id === newOrder.id)
      return originalOrder && originalOrder.stage !== newOrder.column
    })

    // Update each changed order
    for (const changedOrder of changedOrders) {
      try {
        await onOrderStatusChange(changedOrder.id, changedOrder.column)
        
        // Show success toast
        const stageLabel = ORDER_STAGES.find(s => s.id === changedOrder.column)?.label || changedOrder.column
        toast.success(`Updated ${changedOrder.contact.display_name} to ${stageLabel}`)
      } catch (error) {
        // Show error toast
        toast.error('Failed to update order status')
        console.error('Error updating order status:', error)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-sm text-muted-foreground">Loading orders...</div>
      </div>
    )
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center space-y-3 bg-muted/50 h-32 py-8">
        <div className="text-sm font-medium text-muted-foreground">No orders yet</div>
        <div className="text-xs text-muted-foreground">Orders will appear here when they're created</div>
      </div>
    )
  }

  return (
    <div className="h-full">
      <KanbanProvider
        columns={visibleOrderStages}
        data={kanbanOrders}
        onDataChange={handleDataChange}
      >
        {(column) => (
          <KanbanBoard 
            id={column.id} 
            key={column.id}
          >
            <KanbanHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className={column.color}>
                    {column.label}
                  </Badge>
                  <span className="text-sm text-muted-foreground font-normal">
                    {orders.filter(o => o.stage === column.id).length} • {formatSmartCurrency(stageTotals[column.id] || 0)}
                  </span>
                </div>
              </div>
            </KanbanHeader>
            <KanbanCards id={column.id}>
              {(order: KanbanOrder) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onSelectOrder={onSelectOrder}
                  selectedOrderId={selectedOrderId}
                />
              )}
            </KanbanCards>
          </KanbanBoard>
        )}
      </KanbanProvider>
    </div>
  )
}
